import { BoxV2 as Box, Flex, Table, useTable } from 'portal-commons';
import React, { useState } from 'react';
import { ConfirmDeleteModal, DraftRcsCampaignListingRow } from '../components';
import { EmptyFetchedData } from '../constants';
import { DraftRcsCampaign } from '../types';
import { PageTitle } from '@/components';
import Loader from '@/shared_elements/containers/Loader';
import { campaignController } from '@/apis/core';
import { toastFlashMessage } from '@/utils';

const headRows = [
  { id: 'createDate', label: 'Date Created', sortable: true },
  { id: 'brandUid', label: 'Brand ID', sortable: true },
  { id: 'brandName', label: 'Brand DBA Name', sortable: true },
  { id: 'hostingRegion', label: 'Hosting Region', sortable: true },
  { id: 'billingCategory', label: 'Billing Category', sortable: true },
  { id: 'usecase', label: 'Use Case', sortable: true },
  { id: '', label: '', sortable: true },
];

const RcsDraftCampaigns: React.FC = () => {
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedCampaign, setSelectedCampaign] =
    useState<DraftRcsCampaign | null>(null);

  const fetchCampaigns = async () => {
    const queries: Record<string, unknown> = { ...tableFilters };

    const response = await campaignController.getDraftRcsCampaigns(queries);
    if (response) {
      return {
        data: response.records,
        rowsPerPage: response.recordsPerPage,
        total: response.totalRecords,
        page: response.page,
      };
    }
    return EmptyFetchedData;
  };

  const {
    loading: tableLoading,
    pageLoading,
    total: totalCampaigns,
    handleSorting: handleSortingChange,
    handleChangePage: handlePageChange,
    tableData: campaigns,
    pagination,
    filter: tableFilters,
  } = useTable<DraftRcsCampaign>(fetchCampaigns, {
    deps: [],
    defaultFilter: {
      sortField: 'createDate',
      ascendingOrder: false,
    },
  });

  const handleDeleteDraftRcsCampaign = async (campaign: DraftRcsCampaign) => {
    setModalLoading(true);
    try {
      // eslint-disable-next-line no-console
      console.log('Deleting campaign:', campaign);
      // TODO: call API to delete the draft campaign
      // eslint-disable-next-line no-promise-executor-return
      await new Promise((resolve) => setTimeout(resolve, 700));
      setSelectedCampaign(null);
      toastFlashMessage('Draft campaign successfully deleted', 'success');
    } finally {
      setModalLoading(false);
      setDeleteConfirm(false);
    }
  };

  return (
    <>
      <Box
        as="section"
        sx={{
          // Fix dropdown width affected by global styling
          '& .MuiAutocomplete-root': {
            width: 'unset',
          },
        }}
        data-testid="draftRcsCampaignListing"
      >
        {pageLoading ? (
          <Loader />
        ) : (
          <Flex
            sx={{
              flexDirection: 'column',
              alignItems: 'flex-start',
              rowGap: '12px',
            }}
          >
            <PageTitle>
              {totalCampaigns > 0
                ? `${totalCampaigns} Draft RCS Campaigns`
                : 'Draft RCS Campaigns'}
            </PageTitle>
            <Table
              testId="draftRcsCampaignsListingTable"
              loading={tableLoading}
              emptyState="no Draft RCS Campaigns to view"
              handleChangePage={handlePageChange}
              createSortHandler={handleSortingChange}
              headRows={headRows}
              tableData={campaigns.map((campaign: DraftRcsCampaign) => (
                <DraftRcsCampaignListingRow
                  key={campaign.draftId}
                  campaign={campaign}
                  onDelete={() => {
                    setDeleteConfirm(true);
                    setSelectedCampaign(campaign);
                  }}
                />
              ))}
              pagination={pagination}
              headerTextTransform="none"
            />
          </Flex>
        )}
      </Box>
      <ConfirmDeleteModal
        open={deleteConfirm}
        disabled={modalLoading}
        title="Confirm Deletion"
        text={`You are about to permanently delete a draft campaign.<br />Click <strong>Delete</strong> to confirm.`}
        onClose={() => {
          setDeleteConfirm(false);
          setSelectedCampaign(null);
        }}
        onSubmit={() =>
          selectedCampaign && handleDeleteDraftRcsCampaign(selectedCampaign)
        }
      />
    </>
  );
};

export default RcsDraftCampaigns;
